import { create } from "zustand";
import {
  CollectionType,
  Collection,
} from "../../../../../apis/bacchus/collections";
import { CollectionSelectOption } from "../../../../../components/CollectionSelect/CollectionSelect";

interface CollectionDialogState {
  isVisible: boolean;
  editingCollectionId: string | null;
  name: string;
  description: string;
  type: CollectionType;
  country: string;
  items: string[] | number[];
  setIsVisible: (value: boolean) => void;
  openEditMode: (collection: Collection) => void;
  openCreateMode: () => void;
  setName: (name: string) => void;
  setDescription: (description: string) => void;
  setType: (type: CollectionType) => void;
  setCountry: (country: string) => void;
  setItemsFromCollectionOption: (
    selectedCollectionOption: CollectionSelectOption
  ) => void;
  setItems: (items: string[] | number[]) => void;
  reset: () => void;
}

const DEFAULT_NAME = "";
const DEFAULT_DESCRIPTION = "This is a new collection";
const DEFAULT_TYPE = "country" as CollectionType;
const DEFAULT_COUNTRY = "";
const DEFAULT_ITEMS = [] as string[] | number[];

const defaultValues = {
  name: DEFAULT_NAME,
  description: DEFAULT_DESCRIPTION,
  type: DEFAULT_TYPE,
  country: DEFAULT_COUNTRY,
  items: DEFAULT_ITEMS,
};

export const useCollectionDialogStore = create<CollectionDialogState>(
  (set, get) => ({
    isVisible: false,
    editingCollectionId: null,
    ...defaultValues,
    setIsVisible: (value: boolean) =>
      set({
        ...(value === false
          ? { ...defaultValues, editingCollectionId: null }
          : {}),
        isVisible: value,
      }),
    openEditMode: (collection: Collection) => {
      set({
        isVisible: true,
        editingCollectionId: collection.id,
        name: collection.name,
        description: collection.description,
        type: collection.type.replace(
          /^rgif_|_collection$/g,
          ""
        ) as CollectionType,
        country:
          ("countryId" in collection.blob ? collection.blob.countryId : "") ||
          "",
        items: collection.blob.ids,
      });
    },
    openCreateMode: () => {
      set({
        isVisible: true,
        editingCollectionId: null,
        ...defaultValues,
      });
    },
    setName: (name) => set({ name }),
    setDescription: (description) => set({ description }),
    setType: (type) => {
      const currentState = get();
      // Only reset country and items if we're in create mode
      if (!currentState.editingCollectionId) {
        set({
          type,
          country: DEFAULT_COUNTRY,
          items: DEFAULT_ITEMS,
        });
      } else {
        // In edit mode, just update the type without resetting other fields
        set({ type });
      }
    },
    setCountry: (country) => set({ country }),
    setItemsFromCollectionOption: (selectedCollectionOption) => {
      if (
        [
          "country",
          "state",
          "naics",
          "sic",
          "product_category",
          "product_attribute",
          "intent",
        ].includes(get().type)
      ) {
        const currentItems = get().items as string[];
        const collectionItems = selectedCollectionOption.ids as string[];
        const updatedItems = [
          ...currentItems,
          ...collectionItems.filter((i) => !currentItems.includes(i)),
        ];

        set({
          items: updatedItems,
        });

        return;
      }

      const currentItems = get().items as number[];
      const collectionItems = selectedCollectionOption.ids as number[];
      const updatedItems = [
        ...currentItems,
        ...collectionItems.filter((i) => !currentItems.includes(i)),
      ];
      set({
        items: updatedItems,
      });
    },
    setItems: (items) => set({ items }),
    reset: () =>
      set({
        ...defaultValues,
      }),
  })
);
